import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { financeService, type VendorDetail } from '@/services/finance.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const VENDOR_TYPES = ['Fabric', 'Marketing', 'Printing', 'Software', 'Other'];

interface FormValues {
  name: string;
  type: string;
  contact_person: string;
  mobile: string;
  email: string;
  gst_number: string;
  pan: string;
  bank_account_name: string;
  bank_account_number: string;
  ifsc: string;
  upi_id: string;
  credit_limit: string;
  opening_balance: string;
  is_active: boolean;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  vendor?: VendorDetail;
  onClose: () => void;
  onSuccess: () => void;
}

export function VendorFormDialog({ open, mode, vendor, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      name: '', type: 'Other', contact_person: '', mobile: '', email: '',
      gst_number: '', pan: '', bank_account_name: '', bank_account_number: '',
      ifsc: '', upi_id: '', credit_limit: '', opening_balance: '', is_active: true,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: vendor?.name ?? '',
        type: vendor?.type ?? 'Other',
        contact_person: vendor?.contact_person ?? '',
        mobile: vendor?.mobile ?? '',
        email: vendor?.email ?? '',
        gst_number: vendor?.gst_number ?? '',
        pan: vendor?.pan ?? '',
        bank_account_name: vendor?.bank_account_name ?? '',
        bank_account_number: vendor?.bank_account_number ?? '',
        ifsc: vendor?.ifsc ?? '',
        upi_id: vendor?.upi_id ?? '',
        credit_limit: vendor ? String(Number(vendor.credit_limit)) : '',
        opening_balance: '',
        is_active: vendor?.is_active ?? true,
      });
    }
  }, [open, vendor, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const base = {
        name: values.name.trim(),
        type: values.type,
        contact_person: values.contact_person.trim() || null,
        mobile: values.mobile.trim() || null,
        email: values.email.trim() || null,
        gst_number: values.gst_number.trim() || null,
        pan: values.pan.trim() || null,
        bank_account_name: values.bank_account_name.trim() || null,
        bank_account_number: values.bank_account_number.trim() || null,
        ifsc: values.ifsc.trim() || null,
        upi_id: values.upi_id.trim() || null,
        credit_limit: Number(values.credit_limit) || 0,
        is_active: values.is_active,
      };
      if (isEdit && vendor) {
        await financeService.updateVendor(vendor.id, base);
      } else {
        await financeService.createVendor({ ...base, opening_balance: Number(values.opening_balance) || 0 });
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Vendor updated' : 'Vendor created');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'vendors'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
          <DialogDescription>Fill in vendor details</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="v-name">Vendor Name *</Label>
            <Input id="v-name" {...register('name', { required: 'Name is required' })} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="v-type">Vendor Type</Label>
              <Controller
                name="type" control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="v-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VENDOR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-contact">Contact Person</Label>
              <Input id="v-contact" {...register('contact_person')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-mobile">Mobile</Label>
              <Input id="v-mobile" {...register('mobile')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-email">Email</Label>
              <Input id="v-email" type="email" {...register('email')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-gst">GST Number</Label>
              <Input id="v-gst" {...register('gst_number')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-pan">PAN</Label>
              <Input id="v-pan" {...register('pan')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-ban">Bank Account Name</Label>
              <Input id="v-ban" {...register('bank_account_name')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-bacc">Bank Account Number</Label>
              <Input id="v-bacc" {...register('bank_account_number')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-ifsc">IFSC Code</Label>
              <Input id="v-ifsc" {...register('ifsc')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-upi">UPI ID</Label>
              <Input id="v-upi" {...register('upi_id')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-credit">Credit Limit (₹)</Label>
              <Input id="v-credit" type="number" min="0" step="0.01" {...register('credit_limit')} />
            </div>
            {!isEdit && (
              <div className="space-y-1.5">
                <Label htmlFor="v-open">Opening Balance (₹)</Label>
                <Input id="v-open" type="number" min="0" step="0.01" {...register('opening_balance')} />
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('is_active')} className="h-4 w-4 rounded border-input" />
            Active
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update Vendor' : 'Save Vendor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
